import type { FC } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Count,
  defaultThemeProp,
  Flex,
  Icon,
  Progress,
  Text,
  useI18n,
  useUID
} from '@pega/cosmos-react-core';

import FeedButton from './FeedButton';
import FeedLikeButton from './FeedLikeButton';
import type { FeedContentFooterProps } from './Feed.types';

const StyledFeedContentFooter = styled.div(({ theme }) => {
  return css`
    margin-block-start: ${theme.base.spacing};
    margin-inline-end: calc(2 * ${theme.base.spacing});
  `;
});

StyledFeedContentFooter.defaultProps = defaultThemeProp;

const FeedContentFooter: FC<FeedContentFooterProps> = (props: FeedContentFooterProps) => {
  const {
    variant,
    id,
    username,
    liked,
    likes,
    likeCount,
    interactionsEnabled = true,
    likesLoading,
    replies = [],
    replyCount,
    repliesLoading,
    onCommentClick,
    onLikeClick,
    onLikeCountInteraction,
    onLoadMoreLikes,
    onShowMoreReplies,
    onShowLessReplies
  } = props;

  const t = useI18n();
  const commentCountId = useUID();

  const numReplies = replyCount || replies.length;

  return (
    <Flex
      as={StyledFeedContentFooter}
      container={{ justify: 'between', alignItems: 'center', wrap: 'wrap', gap: 1 }}
    >
      {interactionsEnabled ? (
        <Flex container={{ gap: 2 }}>
          <Flex container={{ gap: 0.5, alignItems: 'center' }}>
            <Button
              variant='simple'
              as={FeedButton}
              icon
              compact
              onClick={() => onCommentClick?.({ id, username })}
              label={t('feed_comment_label')}
              aria-describedby={variant === 'post' && numReplies > 0 ? commentCountId : undefined}
            >
              <Icon name='chat' />
            </Button>
            {numReplies > 0 && (
              <Count
                aria-label={t('feed_post_comments', [numReplies], { count: numReplies })}
                id={commentCountId}
              >
                {numReplies}
              </Count>
            )}
          </Flex>
          <FeedLikeButton
            id={id}
            onLikeClick={onLikeClick}
            onLikeCountInteraction={onLikeCountInteraction}
            onLoadMoreLikes={onLoadMoreLikes}
            likesLoading={likesLoading}
            liked={liked}
            likes={likes}
            likeCount={likeCount}
          />
        </Flex>
      ) : (
        <div />
      )}
      {(onShowLessReplies || onShowMoreReplies) && (
        <Flex container={{ gap: 0.5 }}>
          {replies.length !== numReplies && (
            <>
              <Text>{t('showing_num_of_num', [replies.length, numReplies])}</Text>
              <span>&bull;</span>
            </>
          )}
          {onShowMoreReplies && replies.length < numReplies && (
            <Button variant='link' onClick={() => onShowMoreReplies({ id })}>
              {t('show_more')}
            </Button>
          )}
          {repliesLoading && <Progress placement='inline' />}
        </Flex>
      )}
    </Flex>
  );
};

export default FeedContentFooter;
